import { Flooding } from 'features/flooding/flooding.types';
import { useGeoEventEvacuationFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventEvacuationFormFieldChanges';
import {
  useGeoEventLinksFormFieldChanges,
  LINKS_CHANGE_KEYS,
  INTERNAL_LINKS_CHANGE_KEYS,
} from 'features/geoEvents/hooks/useGeoEventLinksFormFieldChanges';
import { useGeoEventMetadataFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventMetadataFormFieldChanges';
import { useGeoEventStatusFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventStatusFormFieldChanges';
import { useGeoEventRegionsChanged } from 'features/geoEvents/hooks/useGeoEventRegionsChanged';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Children, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { Alert, AlertTitle, Grid, Stack, Typography } from '@mui/material';
import { ConfirmCheckbox } from './ConfirmCheckbox';

const MAIN_CHANGE_KEYS = [
  'urgency',
  'reporterOnlyNotes',
  'temporarilyDisplayEvacZones',
  'manualDeactivationStarted',
  'reporterManaged',
  'evacuationOrders',
  'evacuationWarnings',
  'evacuationAdvisories',
  'evacuationShelterInPlace',
  'evacuationNotes',
] as const;

type GeoEventBaseChangelogProps = {
  geoEvent: Wildfire | Flooding;
  evacZones: EvacZone[];
  zoneStyle: RegionEvacZoneStyle;
  initialRegions?: GeoEventRegion[];
  title?: string;
  hideConfirmations?: boolean;
  /**
   * Whether adding or removing regions notifies their subscribers. When it
   * does not, the "confirm added/removed regions" checkboxes are pointless
   * and must not block the update.
   */
  notifiesRegionChanges?: boolean;
  isProvisional?: boolean;
  children?: ReactNode;
};

type ChangeGroupProps = {
  groupId: string;
  changes: Partial<Record<string, string>>;
  keyOrder: readonly string[];
  indentKeys: ReadonlySet<string>;
};

const EMPTY_INDENT_KEYS = new Set<string>();
const GROUP_RENDER_CONFIG = {
  status: {
    keyOrder: ['status'],
    indentKeys: EMPTY_INDENT_KEYS,
  },
  main: {
    keyOrder: [...MAIN_CHANGE_KEYS],
    indentKeys: EMPTY_INDENT_KEYS,
  },
  links: {
    keyOrder: [...LINKS_CHANGE_KEYS],
    indentKeys: new Set(['linksAdded', 'linksRemoved']),
  },
  internalLinks: {
    keyOrder: [...INTERNAL_LINKS_CHANGE_KEYS],
    indentKeys: new Set(['internalLinksAdded', 'internalLinksRemoved']),
  },
} as const;

const ChangeGroup = (props: ChangeGroupProps): JSX.Element => {
  const { groupId, changes, keyOrder, indentKeys } = props;

  return (
    <>
      {keyOrder.map((key) => {
        const text = changes[key];
        if (!text) {
          return null;
        }
        const indent = indentKeys.has(key);
        return (
          <Typography
            key={`${groupId}-${key}`}
            sx={indent ? { pl: 2 } : undefined}
          >
            {text}
          </Typography>
        );
      })}
    </>
  );
};

export const GeoEventBaseChangelog = (
  props: GeoEventBaseChangelogProps,
): JSX.Element | null => {
  const {
    geoEvent,
    evacZones,
    zoneStyle,
    initialRegions,
    title,
    hideConfirmations,
    notifiesRegionChanges = true,
    isProvisional,
    children,
  } = props;
  const { t } = useTranslation();

  const { regionsAdded, regionsRemoved } = useGeoEventRegionsChanged({
    geoEvent,
    initialRegions,
  });

  const statusChanges = useGeoEventStatusFormFieldChanges({ geoEvent });
  const metadataChanges = useGeoEventMetadataFormFieldChanges({ geoEvent });
  const { linksChanges, internalLinksChanges } =
    useGeoEventLinksFormFieldChanges({ geoEvent });

  const {
    evacTextChanges,
    evacuationInfo,
    zoneMismatchLines,
    zoneAddedSection,
    zoneUpgradedSection,
    zoneDowngradedSection,
    zoneRemovedSection,
    hasChanges: hasEvacChanges,
    showConfirmSelectedZones,
  } = useGeoEventEvacuationFormFieldChanges({
    geoEvent,
    evacZones,
    zoneStyle,
    isProvisional,
  });

  const mainChanges = { ...metadataChanges, ...evacTextChanges };

  const hasStatusChanges = Object.keys(statusChanges).length > 0;
  const hasMainChanges = Object.keys(mainChanges).length > 0;
  const hasLinksChanges = Object.keys(linksChanges).length > 0;
  const hasInternalLinksChanges = Object.keys(internalLinksChanges).length > 0;

  const hasChanges =
    hasStatusChanges ||
    regionsAdded ||
    regionsRemoved ||
    hasMainChanges ||
    hasLinksChanges ||
    hasInternalLinksChanges ||
    hasEvacChanges;

  if (!hasChanges && Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Grid>
        <Alert severity="warning" data-testid="changelog-alert">
          <AlertTitle>{title ?? t('geoEventChangelog.title')}</AlertTitle>
          <Stack spacing={2}>
            <Grid>
              {hasStatusChanges && (
                <ChangeGroup
                  groupId="status"
                  changes={statusChanges}
                  keyOrder={GROUP_RENDER_CONFIG.status.keyOrder}
                  indentKeys={GROUP_RENDER_CONFIG.status.indentKeys}
                />
              )}

              {children}

              {hasMainChanges && (
                <ChangeGroup
                  groupId="main"
                  changes={mainChanges}
                  keyOrder={GROUP_RENDER_CONFIG.main.keyOrder}
                  indentKeys={GROUP_RENDER_CONFIG.main.indentKeys}
                />
              )}

              {hasLinksChanges && (
                <ChangeGroup
                  groupId="links"
                  changes={linksChanges}
                  keyOrder={GROUP_RENDER_CONFIG.links.keyOrder}
                  indentKeys={GROUP_RENDER_CONFIG.links.indentKeys}
                />
              )}

              {hasInternalLinksChanges && (
                <ChangeGroup
                  groupId="internalLinks"
                  changes={internalLinksChanges}
                  keyOrder={GROUP_RENDER_CONFIG.internalLinks.keyOrder}
                  indentKeys={GROUP_RENDER_CONFIG.internalLinks.indentKeys}
                />
              )}
            </Grid>

            {evacuationInfo.show && (
              <Grid>
                <Typography>
                  <span>{t('geoEventChangelog.evacuationInfo.line1')}</span>
                  {evacuationInfo.appendSilentLine && (
                    <span>{t('geoEventChangelog.evacuationInfo.line2')}</span>
                  )}
                </Typography>
              </Grid>
            )}

            {zoneMismatchLines.length > 0 && (
              <Grid>
                {zoneMismatchLines.map((line) => (
                  <Typography key={line.id}>
                    <Trans i18nKey={line.i18nKey} values={line.values}>
                      Evacuation zone <b>NCO-E011</b> selected but not
                      referenced in text update.
                    </Trans>
                  </Typography>
                ))}
              </Grid>
            )}

            {zoneAddedSection && (
              <Grid>
                <Typography>{zoneAddedSection.title}</Typography>

                {zoneAddedSection.lines.map((line) => (
                  <Typography key={line.id}>
                    <Trans i18nKey={line.i18nKey} values={line.values}>
                      <b>NCO-E011</b> - <span>Order</span>
                    </Trans>
                  </Typography>
                ))}
              </Grid>
            )}

            {zoneUpgradedSection && (
              <Grid>
                <Typography>{zoneUpgradedSection.title}</Typography>

                {zoneUpgradedSection.lines.map((line) => (
                  <Typography key={line.id}>
                    <Trans i18nKey={line.i18nKey} values={line.values}>
                      <b>NCO-E011</b> - <span>Warning &rarr; Order</span>
                    </Trans>
                  </Typography>
                ))}
              </Grid>
            )}

            {zoneDowngradedSection && (
              <Grid>
                <Typography>{zoneDowngradedSection.title}</Typography>

                {zoneDowngradedSection.lines.map((line) => (
                  <Typography key={line.id}>
                    <Trans i18nKey={line.i18nKey} values={line.values}>
                      <b>NCO-E011</b> - <span>Warning &rarr; Order</span>
                    </Trans>
                  </Typography>
                ))}
              </Grid>
            )}

            {zoneRemovedSection && (
              <Grid>
                <Typography>{zoneRemovedSection.title}</Typography>

                {zoneRemovedSection.zones.map((zone) => (
                  <Typography key={zone.uid}>
                    <b>{zone.displayName}</b>
                  </Typography>
                ))}
              </Grid>
            )}
          </Stack>
        </Alert>
      </Grid>

      {!hideConfirmations && showConfirmSelectedZones && (
        <Grid>
          <ConfirmCheckbox
            fieldName="confirmSelectedZones"
            translationKey="geoEventChangelog.confirmZones"
          />
        </Grid>
      )}
      {!hideConfirmations && regionsAdded && notifiesRegionChanges && (
        <Grid>
          <ConfirmCheckbox
            fieldName="confirmAdditionalRegions"
            translationKey="geoEventChangelog.confirmAddedRegions"
          />
        </Grid>
      )}
      {!hideConfirmations && regionsRemoved && notifiesRegionChanges && (
        <Grid>
          <ConfirmCheckbox
            fieldName="confirmRemovedRegions"
            translationKey="geoEventChangelog.confirmRemovedRegions"
          />
        </Grid>
      )}
    </Stack>
  );
};
